import { useState } from "react";
import { DateTime } from "luxon";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import type { LaunchDateOption } from "@/lib/launchCohort";

interface PimsLaunchDatePickerProps {
  /** ISO date, or null for every site. */
  value: string | null;
  onChange: (date: string | null) => void;
  /** Cumulative site counts at each real go-live date, newest first. */
  options: LaunchDateOption[];
  firstLaunchDate: string | null;
}

// A free date picker rather than a list of go-live dates.
//
// Any date works because the filter is `launch_date <= cutoff`, so picking
// 15 Jul selects exactly the cohort that 14 Jul does -- the last real go-live at
// or before it. That is what makes free-form safe here: there is no date that
// produces a nonsensical answer, only dates that produce the same answer as
// their predecessor.
//
// What the list did give up is the count BEFORE committing. The dropdown showed
// "60 sites" next to each option; a calendar shows nothing until the dashboard
// refetches. The footer restores that, showing the CUMULATIVE count -- the
// number of sites the cutoff keeps, which is the figure that changes what you
// are looking at.
export function PimsLaunchDatePicker({
  value,
  onChange,
  options,
  firstLaunchDate,
}: PimsLaunchDatePickerProps) {
  const [open, setOpen] = useState(false);

  const today = DateTime.now().toISODate()!;
  const selected = value ? DateTime.fromISO(value).toJSDate() : undefined;

  // options is newest-first, so the first entry at or before the date is the
  // latest go-live it covers -- and its cumulative count is the cohort size.
  const cohortAt = (iso: string): number | null =>
    options.find((o) => o.date <= iso)?.sites ?? null;

  const selectedCohort = value ? cohortAt(value) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 text-[12px] font-normal gap-1.5 border-black/15 bg-white"
        >
          <CalendarIcon className="h-3.5 w-3.5" />
          {value ? DateTime.fromISO(value).toFormat("MMM d, yyyy") : "All sites"}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selected}
          defaultMonth={selected}
          // Below the first go-live nothing qualifies and the dashboard empties;
          // after today every launched site qualifies, so the filter is a no-op.
          // Both are dead ends, so neither is offered.
          disabled={{
            ...(firstLaunchDate ? { before: DateTime.fromISO(firstLaunchDate).toJSDate() } : {}),
            after: DateTime.fromISO(today).toJSDate(),
          }}
          onSelect={(d) => {
            if (d) {
              onChange(DateTime.fromJSDate(d).toISODate()!);
              setOpen(false);
            }
          }}
        />
        {/* w-0 with min-w-full so the footer takes the calendar's width without
            contributing to it. The popover is shrink-to-fit and the calendar is
            w-fit at 248px, so a 285px line of footer text was making the popover
            287px wide and leaving 39px of blank space to the right of the grid.
            At zero intrinsic width the footer wraps to the calendar instead. */}
        {/* Only on a selection now. With no cutoff chosen there is nothing to
            report -- the footer used to explain the per-day numbers, and an
            empty bordered strip under the grid is worse than no strip. */}
        {value && (
          <div className="flex w-0 min-w-full flex-col gap-2 border-t border-black/5 p-2">
            <p className="text-[11px] leading-[1.4] text-gray-500">
              {selectedCohort === null ? (
                <>No sites had launched by this date.</>
              ) : (
                <>
                  Keeps <span className="font-semibold text-gray-700">{selectedCohort}</span> sites
                  live by {DateTime.fromISO(value).toFormat("MMM d, yyyy")}.
                </>
              )}
            </p>
            <Button
              variant="outline"
              size="sm"
              className="w-full h-7 text-[12px] font-normal border-black/15"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              All sites
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
